import { type ReactNode, createContext, use, useRef, useMemo, type RefObject } from 'react'

export type PhotosScrollPosition = {
  sort: string
  date: Date
  offset: number
}

type ScrollPositions = Map<string, unknown>

// An auth clear invalidates setters captured by outgoing screens, so their
// unmount cleanup cannot revive a previous account's saved history.
const history_epochs = new WeakMap<ScrollPositions, number>()

const ScrollContext = createContext<RefObject<ScrollPositions>>(undefined as never)

const setPosition = (positions: ScrollPositions, key: string, value: unknown) => {
  positions.delete(key)
  positions.set(key, value)
  if (key.startsWith('history:')) {
    const history_keys = [...positions.keys()].filter(k => k.startsWith('history:'))
    history_keys.slice(0, Math.max(0, history_keys.length - 30)).forEach(k => positions.delete(k))
  }
}

export const useScrollPositionManager = () => {
  const positions = use(ScrollContext).current
  return useMemo(() => ({
    get: <T, >(key: string) => positions.get(key) as T | undefined,
    delete: (key: string) => { positions.delete(key) },
    clearPrefix: (prefix: string) => {
      if ('history:'.startsWith(prefix) || prefix.startsWith('history:')) history_epochs.set(positions, (history_epochs.get(positions) ?? 0) + 1)
      for (const key of positions.keys()) if (key.startsWith(prefix)) positions.delete(key)
    },
  }), [positions])
}

export const ScrollPositionProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const sp = useRef<ScrollPositions>(new Map())
  return <ScrollContext value={sp}>
    {children}
  </ScrollContext>
}

export const useScrollPosition = <T, >(key: string, init_scroll_position: T): {
  get: () => T
  set: (sp: T | ((sp: T)=> T)) => void
} => {
  const scroll_positions = use(ScrollContext).current
  const history_epoch = history_epochs.get(scroll_positions) ?? 0
  if (!scroll_positions.has(key)) setPosition(scroll_positions, key, init_scroll_position)
  return {
    get: () => scroll_positions.get(key) as T,
    set: sp => {
      if (key.startsWith('history:') && history_epoch !== (history_epochs.get(scroll_positions) ?? 0)) return
      setPosition(
        scroll_positions, key,
        sp instanceof Function ? sp(scroll_positions.get(key) as T) : sp
      )
    }
  }
}
